import { Alert, AlertDescription } from '@/components/ui/alert';
import { ServiceCartDrawer } from '@/components/web/cart/service-cart-drawer';
import { formatDate } from '@/lib/date';
import { type BookingRate, type RateDetail } from '@/types/search';
import { type AvailableVoucher } from '@/types/voucher';
import { AlertCircle, ChevronRight, ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { RateCard } from './rate-card';
import { RateDetailDrawer } from './rate-detail-drawer';

interface RateResultsProps {
  rates: BookingRate[];
  siteName: string;
  searchParams: {
    site: string;
    date: string;
  };
  /** Rate-scoped vouchers keyed by `rate.Code` (the rate's mapping_code). */
  rateVouchers?: Record<string, AvailableVoucher[]>;
  className?: string;
  isLoading?: boolean;
  error?: string | null;
}

interface RateGroupProps {
  id: string;
  typeName: string;
  rates: BookingRate[];
  recommended: boolean;
  searchParams: { site: string; date: string };
  rateVouchers?: Record<string, AvailableVoucher[]>;
  onAddToCart: (rate: BookingRate) => void;
  onViewDetail: (rate: BookingRate) => void;
}

function RateGroup({ id, typeName, rates, recommended, searchParams, rateVouchers, onAddToCart, onViewDetail }: RateGroupProps) {
  const [expanded, setExpanded] = useState(true);
  const visible = expanded ? rates : rates.slice(0, 2);
  const hasMore = rates.length > 2;

  return (
    <div id={id} className="mb-8 scroll-mt-40">
      <div className="mb-3 flex items-center gap-2">
        <span className="font-semibold text-gray-900">{typeName}</span>
        {recommended && <span className="rounded-full border border-blue-400 bg-blue-100 px-3 py-0.5 text-xs text-blue-500">Gợi ý cho bạn</span>}
      </div>

      <div className="space-y-4">
        {visible.map((rate, index) => (
          <RateCard
            key={rate.Code}
            rate={rate}
            index={index}
            searchParams={searchParams}
            vouchers={rateVouchers?.[rate.Code]}
            onAddToCart={onAddToCart}
            onViewDetail={onViewDetail}
          />
        ))}
      </div>

      {hasMore && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="text-muted-foreground mt-3 flex items-center gap-1 text-sm font-medium hover:underline"
        >
          {expanded ? (
            <>
              Thu gọn <ChevronUp className="size-4" />
            </>
          ) : (
            <>
              <span>Xem thêm {rates.length - 2} gói</span> <ChevronRight className="size-4" />
            </>
          )}
        </button>
      )}
    </div>
  );
}

export function RateResults({ rates, siteName, searchParams, rateVouchers, className, error = null }: RateResultsProps) {
  const [selectedRate, setSelectedRate] = useState<BookingRate | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedMappingCode, setSelectedMappingCode] = useState<string | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

  const handleAddToCart = (rate: BookingRate) => {
    setSelectedRate(rate);
    setIsDrawerOpen(true);
  };

  const handleViewDetail = (rate: BookingRate) => {
    setSelectedMappingCode(rate.Code);
    setIsDetailDrawerOpen(true);
  };

  const handleSelectServicesFromDetail = (rateDetail: RateDetail) => {
    const bookingRate: BookingRate = {
      Code: rateDetail.Code,
      Name: rateDetail.Name,
      DisplayName: rateDetail.DisplayName,
      TypeName: rateDetail.TypeName,
      MinTicketBooking: rateDetail.MinTicketBooking,
      RateDiscountPercent: rateDetail.RateDiscountPercent,
      Services: rateDetail.Services.map((service) => ({
        Code: service.Code,
        Name: service.Name,
        DisplayName: service.DisplayName,
        ShortName: service.ShortName,
        TypeName: service.TypeName,
        BasePrice: service.BasePrice,
        Price: service.Price,
        MinTicketBooking: service.MinTicketBooking,
        RateDiscountPercent: service.RateDiscountPercent,
      })),
    };
    setIsDetailDrawerOpen(false);
    setSelectedRate(bookingRate);
    setIsDrawerOpen(true);
  };

  // Derive unique categories in order (used to generate stable group IDs)
  const categories = useMemo(() => {
    if (!rates?.length) return [];
    return [...new Set(rates.map((r) => r.TypeName).filter((t): t is string => Boolean(t)))];
  }, [rates]);

  const grouped = useMemo(() => {
    if (!rates?.length) return [];
    const map = rates.reduce<Record<string, BookingRate[]>>((acc, r) => {
      const key = r.TypeName ?? 'Khác';
      (acc[key] ??= []).push(r);
      return acc;
    }, {});
    return Object.entries(map);
  }, [rates]);

  if (error) {
    return (
      <div className={className}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!rates || rates.length === 0) {
    return (
      <div className={className}>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Không tìm thấy gói dịch vụ phù hợp tại {siteName}. Vui lòng thử lại với địa điểm hoặc ngày khác.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={className}>
      {grouped.map(([typeName, groupRates]) => {
        const groupIndex = categories.indexOf(typeName);
        return (
          <RateGroup
            key={typeName}
            id={`rate-group-${groupIndex}`}
            typeName={typeName}
            rates={groupRates}
            recommended={groupIndex === 0}
            searchParams={searchParams}
            rateVouchers={rateVouchers}
            onAddToCart={handleAddToCart}
            onViewDetail={handleViewDetail}
          />
        );
      })}

      <RateDetailDrawer
        open={isDetailDrawerOpen}
        onOpenChange={setIsDetailDrawerOpen}
        mappingCode={selectedMappingCode}
        onSelectServices={handleSelectServicesFromDetail}
      />

      <ServiceCartDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        rate={selectedRate}
        searchParams={{
          site: searchParams.site,
          date: formatDate(searchParams.date, 'dd/MM/yyyy'),
        }}
      />
    </div>
  );
}
